import React, { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { LoginCredentials } from '../../types';
import { storeAuthData } from '../../utils/auth';
import './Auth.css';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!credentials.username || !credentials.password) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiService.login(credentials);

      if (response.success && response.token && response.user) {
        storeAuthData(response.token, response.user);
        navigate('/dashboard');
      } else {
        setError(response.message || 'Login failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* WhatsApp green top bar */}
      <div className="auth-top-bar">
        <svg viewBox="0 0 39 11" width="39" height="11">
          <path fill="white" d="M10.617 0.462l-4.209 10.076h-2.16L0.015 0.462h2.129L4.74 8.328h0.046L7.326 0.462H10.617zM17.269 10.538h-1.975V9.476h-0.046c-0.499 0.783-1.244 1.243-2.344 1.243-1.682 0-2.797-1.071-2.797-2.904V3.278h2.068v4.259c0 1.03 0.576 1.605 1.529 1.605 0.983 0 1.598-0.637 1.598-1.605V3.278h2.068v7.26H17.269zM25.278 10.538h-2.068V6.28c0-1.029-0.576-1.605-1.529-1.605-0.983 0-1.598 0.637-1.598 1.605v4.258h-2.068V0.462h2.068v3.906h0.046c0.499-0.783 1.244-1.244 2.344-1.244 1.682 0 2.797 1.072 2.797 2.905v4.509H25.278z"></path>
        </svg>
        <span className="auth-top-bar-title">WHATSAPP WEB</span>
      </div>

      <div className="auth-card">
        <div className="auth-header">
          {/* WhatsApp icon */}
          <div className="auth-header-icon">
            <svg viewBox="0 0 24 24" width="44" height="44">
              <path fill="white" d="M19.005 3.175H4.674C3.642 3.175 3 3.789 3 4.821V21.02l3.544-3.514h12.461c1.033 0 2.064-1.06 2.064-2.093V4.821c-.001-1.032-1.032-1.646-2.064-1.646zm-4.989 9.869H7.041V11.1h6.975v1.944zm3-4H7.041V7.1h9.975v1.944z"></path>
            </svg>
          </div>
          <h1>Sign in to WhatsApp</h1>
          <p>Enter your credentials to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={credentials.username}
              onChange={handleInputChange}
              placeholder="Enter your username"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={credentials.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>Contact your administrator for account access.</p>
        </div>

        <div className="auth-encryption-note">
          <svg viewBox="0 0 10 12" width="10" height="12">
            <path fill="currentColor" d="M5.009 0C3.351 0 2.008 1.343 2.008 3.001V4.5H.506v6.5c0 .55.45 1 1.001 1h7.005c.55 0 1.001-.45 1.001-1V4.5H8.011V3.001C8.011 1.343 6.668 0 5.009 0zm0 1.5c.831 0 1.502.671 1.502 1.501V4.5H3.507V3.001c0-.83.671-1.501 1.502-1.501z"></path>
          </svg>
          <span>End-to-end encrypted</span>
        </div>
      </div>
    </div>
  );
};

export default Login;
